import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { apiService } from '../../services/api';
import ConfigurableTable, { TableColumn, ColumnFilter } from '../common/ConfigurableTable';
import { useDebouncedCallback } from '../../hooks/useDebounced';
import EnhancedSearchInput from '../common/EnhancedSearchInput';

interface Company {
  id: string;
  name: string;
  aliases: string[];
  description: string | null;
  primaryEmail: string | null;
  primaryPhone: string | null;
  website: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  country: string | null;
  keyTimeFrame: string | null;
  type: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
  customerCount: number;
  ticketCount: number;
  deviceCount: number;
  serviceCount: number;
  lastTicketDate: string | null;
  customers: any[];
  tickets: any[];
  devices: any[];
}

interface CompanyListProps {
  onCompanySelect: (company: Company) => void;
  onCompanyEdit?: (company: Company) => void;
}

const CompanyList: React.FC<CompanyListProps> = ({ onCompanySelect, onCompanyEdit }) => {
  const { user } = useAuth();
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [columnFilters, setColumnFilters] = useState<ColumnFilter[]>([]);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(50);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  
  // Sorting state
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; company: Company | null }>({
    show: false,
    company: null
  });
  const [deleting, setDeleting] = useState(false);

  // Create company form
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCompany, setNewCompany] = useState({
    name: '',
    aliases: [''],
    description: '',
    primaryEmail: '',
    primaryPhone: '',
    website: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    keyTimeFrame: ''
  });

  // Load user preferences from localStorage
  const loadPreferences = () => {
    const storageKey = 'company-list-preferences';
    const defaultPrefs = {
      itemsPerPage: 50,
      sortBy: 'name',
      sortOrder: 'asc',
      searchTerm: '',
      countryFilter: '',
    };

    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          itemsPerPage: parsed.itemsPerPage || defaultPrefs.itemsPerPage,
          sortBy: parsed.sortBy || defaultPrefs.sortBy,
          sortOrder: parsed.sortOrder || defaultPrefs.sortOrder,
          searchTerm: parsed.searchTerm || defaultPrefs.searchTerm,
          countryFilter: parsed.countryFilter || defaultPrefs.countryFilter,
        };
      }
    } catch (error) {
      console.error('Error loading company preferences:', error);
    }
    return defaultPrefs;
  };

  // Save user preferences to localStorage
  const savePreferences = useCallback((preferences: any) => {
    try {
      const storageKey = 'company-list-preferences';
      localStorage.setItem(storageKey, JSON.stringify(preferences));
    } catch (error) {
      console.error('Error saving company preferences:', error);
    }
  }, []);

  // Debounced save preferences
  const debouncedSavePreferences = useDebouncedCallback(
    (preferences: any) => {
      savePreferences(preferences);
    },
    1000,
    [savePreferences]
  );

  // Stable search handler to prevent re-renders and focus loss
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  // Auto-save preferences when they change
  useEffect(() => {
    debouncedSavePreferences({
      itemsPerPage,
      sortBy,
      sortOrder,
      searchTerm,
      countryFilter,
    });
  }, [itemsPerPage, sortBy, sortOrder, searchTerm, countryFilter, debouncedSavePreferences]);

  const loadCompanies = useDebouncedCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      const filters = {
        search: searchTerm,
        country: countryFilter,
        sortBy,
        sortOrder,
        page: currentPage,
        limit: itemsPerPage,
      };

      const result = await apiService.getCompanies(filters);
      setCompanies(result.companies);
      setTotalItems(result.pagination.total);
      setTotalPages(result.pagination.totalPages);
      setCurrentPage(result.pagination.page);
    } catch (err) {
      console.error('Error loading companies:', err);
      setError(err instanceof Error ? err.message : 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, 800, []); // Increased delay and removed dependencies to make it stable

  // Track if preferences have been loaded
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);

  // Load preferences on mount
  useEffect(() => {
    const preferences = loadPreferences();
    setItemsPerPage(preferences.itemsPerPage);
    setSortBy(preferences.sortBy);
    setSortOrder(preferences.sortOrder);
    setSearchTerm(preferences.searchTerm);
    setCountryFilter(preferences.countryFilter);
    setPreferencesLoaded(true);
  }, []);

  // Load companies when filters or pagination changes, but only after preferences are loaded
  useEffect(() => {
    if (preferencesLoaded) {
      loadCompanies();
    }
  }, [preferencesLoaded, searchTerm, countryFilter, sortBy, sortOrder, currentPage, itemsPerPage, loadCompanies]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(1); // Reset to first page when changing items per page
  };



  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatRelativeTime = (dateString: string | null) => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const handleDeleteCompany = (company: Company) => {
    // Check if user has delete permission
    if (!user?.permissions?.includes('companies.delete')) {
      alert('You do not have permission to delete companies.');
      return;
    }
    setDeleteConfirm({ show: true, company });
  };

  const confirmDelete = async () => {
    if (!deleteConfirm.company) return;
    
    try {
      setDeleting(true);
      await apiService.deleteCompany(deleteConfirm.company.id);
      setCompanies(companies.filter(c => c.id !== deleteConfirm.company!.id));
      setDeleteConfirm({ show: false, company: null });
    } catch (error) {
      console.error('Error deleting company:', error);
      alert('Failed to delete company. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleCreateCompany = async () => {
    try {
      const companyData = {
        ...newCompany,
        aliases: newCompany.aliases.filter(alias => alias.trim() !== '')
      };
      
      await apiService.createCompany(companyData);
      setShowCreateForm(false);
      setNewCompany({
        name: '',
        aliases: [''],
        description: '',
        primaryEmail: '',
        primaryPhone: '',
        website: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        keyTimeFrame: ''
      });
      loadCompanies();
    } catch (error) {
      console.error('Error creating company:', error);
      alert('Failed to create company. Please try again.');
    }
  };

  // Define table columns
  const columns: TableColumn<Company>[] = [
    {
      id: 'name',
      label: 'Company Name',
      width: 250,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (company) => (
        <div className="text-sm min-w-0">
          <div className="font-medium text-gray-900 dark:text-white truncate" title={company.name}>
            {company.name}
          </div>
          {company.aliases && company.aliases.length > 0 && (
            <div className="text-gray-500 dark:text-gray-400 text-xs truncate" title={`AKA: ${company.aliases.join(', ')}`}>
              AKA: {company.aliases.slice(0, 2).join(', ')}
              {company.aliases.length > 2 && ` +${company.aliases.length - 2} more`}
            </div>
          )}
        </div>
      ),
    },
    {
      id: 'primaryEmail',
      label: 'Primary Email',
      width: 200,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (company) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={company.primaryEmail || '-'}>
          {company.primaryEmail || '-'}
        </div>
      ),
    },
    {
      id: 'primaryPhone',
      label: 'Phone',
      width: 150,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (company) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={company.primaryPhone || '-'}>
          {company.primaryPhone || '-'}
        </div>
      ),
    },
    {
      id: 'city',
      label: 'City',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (company) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={company.city || '-'}>
          {company.city || '-'}
        </div>
      ),
    },
    {
      id: 'country',
      label: 'Country',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      render: (company) => (
        <div className="text-sm text-gray-900 dark:text-white truncate" title={company.country || '-'}>
          {company.country || '-'}
        </div>
      ),
    },
    {
      id: 'type',
      label: 'Type',
      width: 150,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'Standard', label: 'Standard' },
        { value: 'NVDB', label: 'NVDB Import' }
      ],
      render: (company) => (
        <div className="text-sm">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            company.type.startsWith('NVDB') 
              ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/50 dark:text-indigo-200' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {company.type}
          </span>
        </div>
      ),
    },
    {
      id: 'customerCount',
      label: 'Customers',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'number',
      render: (company) => (
        <div className="text-sm text-center">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            company.customerCount > 0 
              ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-200' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {company.customerCount}
          </span>
        </div>
      ),
    },
    {
      id: 'ticketCount',
      label: 'Tickets',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'number',
      render: (company) => (
        <div className="text-sm text-center">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            company.ticketCount > 0 
              ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {company.ticketCount}
          </span>
        </div>
      ),
    },
    {
      id: 'deviceCount',
      label: 'Devices',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'number',
      render: (company) => (
        <div className="text-sm text-center">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            company.deviceCount > 0 
              ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-200' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {company.deviceCount}
          </span>
        </div>
      ),
    },
    {
      id: 'serviceCount',
      label: 'Services',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'number',
      render: (company) => (
        <div className="text-sm text-center">
          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
            company.serviceCount > 0 
              ? 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-200' 
              : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200'
          }`}>
            {company.serviceCount}
          </span>
        </div>
      ),
    },
    {
      id: 'lastTicketDate',
      label: 'Last Ticket',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (company) => (
        <div className="text-sm">
          <div className="text-gray-900 dark:text-white">
            {formatDate(company.lastTicketDate)}
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">
            {formatRelativeTime(company.lastTicketDate)}
          </div>
        </div>
      ),
    },
    {
      id: 'keyTimeFrame',
      label: 'Key Time Frame',
      width: 150,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'text',
      render: (company) => (
        <div className="text-sm text-gray-900 dark:text-white">
          {company.keyTimeFrame || '-'}
        </div>
      ),
    },
    {
      id: 'createdAt',
      label: 'Created On',
      width: 120,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (company) => (
        <div className="text-sm">
          <div className="text-gray-900 dark:text-white">
            {formatDate(company.createdAt)}
          </div>
          <div className="text-gray-500 dark:text-gray-400 text-xs">
            {formatRelativeTime(company.createdAt)}
          </div>
        </div>
      ),
    },
    {
      id: 'isActive',
      label: 'Status',
      width: 100,
      visible: true,
      resizable: true,
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'true', label: 'Active' },
        { value: 'false', label: 'Inactive' }
      ],
      render: (company) => (
        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
          company.isActive
            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-200'
            : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200'
        }`}>
          {company.isActive ? 'Active' : 'Inactive'}
        </span>
      ),
    },
  ];



  // Export function
  const handleExport = () => {
    const exportData = companies.map(company => ({
      'Company Name': company.name,
      'Aliases': company.aliases?.join(', ') || 'None',
      'Primary Email': company.primaryEmail || '-',
      'Primary Phone': company.primaryPhone || '-',
      'Website': company.website || '-',
      'Address': [company.address, company.city, company.state, company.zipCode, company.country]
        .filter(Boolean).join(', ') || '-',
      'Key Time Frame': company.keyTimeFrame || '-',
      'Customer Count': company.customerCount,
      'Ticket Count': company.ticketCount,
      'Device Count': company.deviceCount,
      'Service Count': company.serviceCount,
      'Last Ticket Date': formatDate(company.lastTicketDate),
      'Status': company.isActive ? 'Active' : 'Inactive',
      'Created Date': formatDate(company.createdAt),
      'Description': company.description || '-',
    }));

    const XLSX = require('xlsx');
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Companies');
    
    const filename = `companies-${new Date().toISOString().split('T')[0]}.xlsx`;
    XLSX.writeFile(wb, filename);
  };



  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="flex">
          <div className="flex-shrink-0">
            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
            </svg>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-red-800 dark:text-red-200">Error loading companies</h3>
            <p className="mt-2 text-sm text-red-700 dark:text-red-300">{error}</p>
            <button
              onClick={loadCompanies}
              className="mt-2 bg-red-100 hover:bg-red-200 dark:bg-red-800 dark:hover:bg-red-700 px-3 py-1 rounded text-sm text-red-800 dark:text-red-200"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

 return (
    <div className="space-y-6">
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border-l-4 border-red-400 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700 dark:text-red-200">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Search Section */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1 max-w-md">
            <label htmlFor="company-search" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Search Companies
            </label>
            <EnhancedSearchInput
              value={searchTerm}
              onChange={handleSearchChange}
              placeholder="Search by name, email, phone, address, city, country, website, aliases... (use &quot;quotes&quot; for exact matches)"
              showSearchTips={true}
              debounceMs={300}
            />
          </div>
          <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
            <span>{totalItems} companies found</span>
          </div>
        </div>
      </div>

      <ConfigurableTable
        data={companies}
        columns={columns}
        loading={loading}
        storageKey="companies-table"
        title="Companies"
        searchTerm=""
        onSearchChange={undefined}
        columnFilters={columnFilters}
        onColumnFiltersChange={setColumnFilters}
        onRowClick={onCompanySelect}
        onExport={handleExport}
        exportFilename="companies"
        sortBy={sortBy}
        sortOrder={sortOrder}
        onSortChange={(newSortBy, newSortOrder) => {
          setSortBy(newSortBy);
          setSortOrder(newSortOrder);
          setCurrentPage(1); // Reset to first page when sorting changes
        }}
        actions={[
          {
            label: 'Edit',
            icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
            ),
            onClick: (company) => onCompanyEdit?.(company),
            show: () => (user?.role === 'agent' && user?.permissions?.includes('companies.edit')) || user?.roleName === 'Admin',
            className: 'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300'
          },
          {
            label: 'Delete',
            icon: (
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
            ),
            onClick: handleDeleteCompany,
            show: () => (user?.role === 'agent' && user?.permissions?.includes('companies.delete')) || user?.roleName === 'Admin',
            className: 'text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300'
          }
        ]}
        headerActions={
          user?.permissions?.includes('companies.create') && (
            <button
              onClick={() => setShowCreateForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              <svg className="w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Add Company
            </button>
          )
        }
        emptyState={
          <div className="text-center py-12">
            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No companies found</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm || columnFilters.length > 0 ? 'Try adjusting your search or filters.' : 'Get started by creating a new company.'}
            </p>
          </div>
        }
      />

      {/* Pagination Controls - Positioned below the table */}
      {totalItems > 0 && (
        <div className="flex items-center justify-between px-6 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 rounded-b-md">
          <div className="flex items-center space-x-4">
            <div className="flex items-center space-x-2">
              <span className="text-sm text-gray-700 dark:text-gray-300">Items per page:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                className="border border-gray-300 dark:border-gray-600 rounded-md px-2 py-1 text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>
            
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Showing {Math.min(((currentPage - 1) * itemsPerPage) + 1, totalItems)} to {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems} entries
            </span>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage <= 1}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Previous
            </button>
            
            {/* Page numbers */}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const pageNumber = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return (
                <button
                  key={pageNumber}
                  onClick={() => handlePageChange(pageNumber)}
                  className={`px-3 py-1 text-sm border rounded-md ${
                    pageNumber === currentPage
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {pageNumber}
                </button>
              );
            })}
            
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages}
              className="px-3 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Create Company Modal */}
      {showCreateForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-96 max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Create New Company</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Company Name *</label>
                  <input
                    type="text"
                    value={newCompany.name}
                    onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
                  <textarea
                    value={newCompany.description}
                    onChange={(e) => setNewCompany({...newCompany, description: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Key Time Frame</label>
                  <textarea
                    value={newCompany.keyTimeFrame}
                    onChange={(e) => setNewCompany({...newCompany, keyTimeFrame: e.target.value})}
                    rows={3}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Enter key time frame information..."
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Primary Email</label>
                    <input
                      type="email"
                      value={newCompany.primaryEmail}
                      onChange={(e) => setNewCompany({...newCompany, primaryEmail: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Primary Phone</label>
                    <input
                      type="tel"
                      value={newCompany.primaryPhone}
                      onChange={(e) => setNewCompany({...newCompany, primaryPhone: e.target.value})}
                      className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Country</label>
                  <input
                    type="text"
                    value={newCompany.country}
                    onChange={(e) => setNewCompany({...newCompany, country: e.target.value})}
                    className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              
              <div className="flex justify-end space-x-4 mt-6">
                <button
                  onClick={() => setShowCreateForm(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateCompany}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                  disabled={!newCompany.name.trim()}
                >
                  Create Company
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white dark:bg-gray-800">
            <div className="mt-3 text-center">
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20">
                <svg className="h-6 w-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 dark:text-white mt-5">Delete Company</h3>
              <div className="mt-2 px-7 py-3">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Are you sure you want to delete "{deleteConfirm.company?.name}"? 
                  This action cannot be undone.
                </p>
              </div>
              <div className="flex justify-center space-x-4 mt-4">
                <button
                  onClick={() => setDeleteConfirm({ show: false, company: null })}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-300 rounded hover:bg-gray-400 dark:hover:bg-gray-500"
                  disabled={deleting}
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                  disabled={deleting}
                >
                  {deleting ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CompanyList; 